import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../constants/AuthContext';
import { COLORS } from '../constants/theme';
import { LayoutDashboard, Package, PlusCircle, Receipt, BarChart2, Users, ChevronLeft, ChevronRight, LogOut, Key } from 'lucide-react';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, ownerOnly: false },
  { path: '/products', label: 'Products', icon: Package, ownerOnly: false },
  { path: '/record', label: 'Record Sale', icon: PlusCircle, ownerOnly: false },
  { path: '/history', label: 'History', icon: Receipt, ownerOnly: false },
  { path: '/reports', label: 'Reports', icon: BarChart2, ownerOnly: false },
  { path: '/staff', label: 'Staff', icon: Users, ownerOnly: true },
];

export default function Sidebar({ collapsed, onToggle }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div style={{ width: collapsed ? 64 : 260, minHeight: '100vh', background: COLORS.ink, display: 'flex', flexDirection: 'column', position: 'fixed', left: 0, top: 0, bottom: 0, zIndex: 100, transition: 'width 0.25s ease', overflow: 'hidden' }}>

      {/* Logo + collapse */}
      <div style={{ padding: collapsed ? '24px 16px' : '28px 24px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'space-between' }}>
        {!collapsed && (
          <div style={{ fontFamily: 'sans-serif', fontSize: 20, fontWeight: 900, letterSpacing: -0.5, whiteSpace: 'nowrap' }}>
            <span style={{ color: 'white' }}>Stok</span>
            <span style={{ color: '#3DAA6A' }}>Pilot</span>
          </div>
        )}
        <button
          onClick={onToggle}
          style={{ background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: 8, width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
        >
          {collapsed ? <ChevronRight size={16} color="rgba(255,255,255,0.6)" /> : <ChevronLeft size={16} color="rgba(255,255,255,0.6)" />}
        </button>
      </div>

      {/* User info */}
      {!collapsed && (
        <div style={{ padding: '16px 24px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 12, background: COLORS.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 900, color: 'white', flexShrink: 0 }}>
              {user?.fullName?.charAt(0).toUpperCase()}
            </div>
            <div style={{ overflow: 'hidden' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'white', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.fullName}</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>{user?.role} · {user?.businessName}</div>
            </div>
          </div>
        </div>
      )}

      {/* Avatar collapsed */}
      {collapsed && (
        <div style={{ padding: '12px', display: 'flex', justifyContent: 'center', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: COLORS.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 900, color: 'white' }}>
            {user?.fullName?.charAt(0).toUpperCase()}
          </div>
        </div>
      )}

      {/* Nav */}
      <div style={{ flex: 1, padding: collapsed ? '16px 8px' : '16px 12px', display: 'flex', flexDirection: 'column', gap: 4 }}>
        {!collapsed && (
          <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.3)', letterSpacing: 1.5, padding: '8px 12px', textTransform: 'uppercase' }}>Main</div>
        )}
        {navItems
          .filter(item => !item.ownerOnly || user?.role === 'Owner')
          .map(item => {
            const active = location.pathname === item.path;
            const Icon = item.icon;
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                title={collapsed ? item.label : ''}
                style={{ display: 'flex', alignItems: 'center', gap: collapsed ? 0 : 12, justifyContent: collapsed ? 'center' : 'flex-start', padding: collapsed ? '11px' : '11px 14px', borderRadius: 10, border: 'none', cursor: 'pointer', background: active ? COLORS.accent : 'transparent', color: active ? 'white' : 'rgba(255,255,255,0.55)', fontSize: 14, fontWeight: active ? 700 : 500, textAlign: 'left', width: '100%', transition: 'all 0.15s', whiteSpace: 'nowrap' }}
                onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
              >
                <Icon size={18} strokeWidth={active ? 2.5 : 1.8} style={{ flexShrink: 0 }} />
                {!collapsed && item.label}
              </button>
            );
          })}
      </div>

      {/* Business code — owner only */}
      {!collapsed && user?.role === 'Owner' && (
        <div style={{ padding: '12px 24px', borderTop: '1px solid rgba(255,255,255,0.08)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            <Key size={10} color="rgba(255,255,255,0.3)" />
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', fontWeight: 700, letterSpacing: 1 }}>BUSINESS CODE</div>
          </div>
          <div style={{ fontSize: 15, fontWeight: 900, color: '#3DAA6A', letterSpacing: 2 }}>{user?.businessCode}</div>
        </div>
      )}

      {/* Logout */}
      <div style={{ padding: collapsed ? '8px' : '12px' }}>
        <button
          onClick={handleLogout}
          title={collapsed ? 'Logout' : ''}
          style={{ display: 'flex', alignItems: 'center', gap: collapsed ? 0 : 10, justifyContent: collapsed ? 'center' : 'flex-start', padding: collapsed ? '11px' : '11px 14px', borderRadius: 10, border: 'none', cursor: 'pointer', background: 'transparent', color: '#f87171', fontSize: 14, fontWeight: 600, width: '100%', transition: 'all 0.15s' }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(248,113,113,0.1)'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
          <LogOut size={18} style={{ flexShrink: 0 }} />
          {!collapsed && 'Logout'}
        </button>
      </div>
    </div>
  );
}